import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Close, Refresh } from "@material-ui/icons";

import TeamPhotosListView from "./components/teamPhotosListView";
import SessionDetail from "./components/sessionDetail";
import MainAppBar from "../../components/MainAppBar";
import ApiUser from "../../../API/ApiUser";
import {
  formatTime,
  isSessionLive,
  POLL_INTERVAL_MS,
} from "./sessionUtils";

const TeamPhotosView = ({
  state,
  classes,
  onPageChange,
  onRowsPerPageChange,
  onSortColumn,
  onDeleteTeamPhoto,
  onRefreshNow,
}) => {
  const { t } = useTranslation();
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    ApiUser.isAdmin().then((response) => {
      if (!cancelled && response.status === 200) {
        setIsAdmin(response.data.isAdmin);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Le détail lit la session dans la liste rafraîchie : une partie en cours se
  // met donc à jour toute seule pendant qu'on la regarde.
  const selectedSession =
    state.sessions.find((session) => session.id === selectedSessionId) || null;

  const liveCount = state.sessions.filter(isSessionLive).length;

  if (state.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <>
      <MainAppBar
        headerText={t("newTour.title")}
        showHomeButton={true}
        showAdminButton={isAdmin}
      />
      <Box className={`${classes.root} ${classes.mainContent}`}>
        <Box className={classes.pannel}>
          <Box display="flex" alignItems="center" mb={3}>
            <Typography variant="h4" className={classes.title}>
              {t("teamPhotos.title")}
            </Typography>
          </Box>

          {state.tour && (
            <Box mb={3}>
              <Typography variant="h6" className={classes.tourTitle}>
                {state.tour.title}
              </Typography>
            </Box>
          )}

          {state.showError && (
            <Box mb={2}>
              <Alert severity="error">{state.errorMessage}</Alert>
            </Box>
          )}

          <div className={classes.refreshBar}>
            <Box display="flex" alignItems="center">
              {liveCount > 0 ? (
                <span className={classes.liveCount}>
                  <span className={classes.liveDot} />
                  {t("teamPhotos.refresh.live_sessions", { count: liveCount })}
                </span>
              ) : (
                <Typography className={classes.refreshHint}>
                  {t("teamPhotos.refresh.no_live_session")}
                </Typography>
              )}
            </Box>

            <Box display="flex" alignItems="center">
              <Typography className={classes.refreshHint}>
                {t("teamPhotos.refresh.every", {
                  seconds: POLL_INTERVAL_MS / 1000,
                })}
                {state.lastRefreshedAt
                  ? ` · ${t("teamPhotos.refresh.last_updated", {
                      time: formatTime(state.lastRefreshedAt),
                    })}`
                  : ""}
              </Typography>
              <IconButton
                size="small"
                onClick={onRefreshNow}
                disabled={state.refreshing}
                title={t("teamPhotos.refresh.refresh_now")}
              >
                {state.refreshing ? (
                  <CircularProgress size={18} />
                ) : (
                  <Refresh fontSize="small" />
                )}
              </IconButton>
            </Box>
          </div>

          <TeamPhotosListView
            state={state}
            classes={classes}
            onSessionClick={setSelectedSessionId}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            onSortColumn={onSortColumn}
          />

          <Dialog
            open={!!selectedSession}
            onClose={() => setSelectedSessionId(null)}
            maxWidth="lg"
            fullWidth
            className={classes.carouselDialog}
            disableRestoreFocus
          >
            <DialogTitle className={classes.carouselTitle}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">
                  {t("teamPhotos.detail.title")}
                </Typography>
                <IconButton onClick={() => setSelectedSessionId(null)}>
                  <Close style={{ color: "white" }} />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent className={classes.detailContent}>
              <SessionDetail
                session={selectedSession}
                classes={classes}
                onDeletePhoto={onDeleteTeamPhoto}
              />
            </DialogContent>
          </Dialog>
        </Box>
      </Box>
    </>
  );
};

export default TeamPhotosView;
